import React, { useEffect, useRef } from 'react';
import { FiBook, FiBookOpen, FiAward, FiTarget, FiUsers, FiStar } from 'react-icons/fi';
import { EducationalParticlesContainer, Particle } from './EducationalParticles.styles';

const EducationalParticles = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const particles = [];
    const particleCount = 12;

    // Educational icons with React components
    const icons = [
      { component: FiBook, size: 20, color: '#2c5f34' },
      { component: FiBookOpen, size: 18, color: '#10B981' },
      { component: FiStar, size: 22, color: '#F59E0B' },
      { component: FiAward, size: 16, color: '#EF4444' },
      { component: FiTarget, size: 19, color: '#8B5CF6' },
      { component: FiUsers, size: 17, color: '#06B6D4' },
    ];

    // Create particles
    for (let i = 0; i < particleCount; i++) {
      const particle = document.createElement('div');
      const randomIcon = icons[Math.floor(Math.random() * icons.length)];
      
      particle.className = 'educational-particle';
      particle.style.position = 'absolute';
      particle.style.left = Math.random() * 100 + '%';
      particle.style.top = Math.random() * 100 + '%';
      particle.style.opacity = '0.08';
      particle.style.pointerEvents = 'none';
      particle.style.transition = 'all 0.3s ease-out';
      particle.style.zIndex = '1';
      particle.style.color = randomIcon.color;
      particle.style.fontSize = randomIcon.size + 'px';
      particle.style.display = 'flex';
      particle.style.alignItems = 'center';
      particle.style.justifyContent = 'center';
      
      // Add simple text representation for now
      particle.textContent = getIconText(randomIcon.component.name);
      
      container.appendChild(particle);
      
      particles.push({
        element: particle,
        x: Math.random() * 100,
        y: Math.random() * 100,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        size: randomIcon.size,
        color: randomIcon.color,
      });
    }

    // Animation loop
    let animationId;
    const animate = () => {
      particles.forEach(particle => {
        particle.x += particle.vx;
        particle.y += particle.vy;

        // Bounce off edges
        if (particle.x <= 0 || particle.x >= 100) particle.vx *= -1;
        if (particle.y <= 0 || particle.y >= 100) particle.vy *= -1;

        // Keep within bounds
        particle.x = Math.max(0, Math.min(100, particle.x));
        particle.y = Math.max(0, Math.min(100, particle.y));

        particle.element.style.left = particle.x + '%';
        particle.element.style.top = particle.y + '%';
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();

    // Mouse interaction
    const handleMouseMove = (e) => {
      const rect = container.getBoundingClientRect();
      const mouseX = ((e.clientX - rect.left) / rect.width) * 100;
      const mouseY = ((e.clientY - rect.top) / rect.height) * 100;

      particles.forEach(particle => {
        const dx = mouseX - particle.x;
        const dy = mouseY - particle.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 25) {
          const force = (25 - distance) / 25;
          particle.vx += dx * force * 0.005;
          particle.vy += dy * force * 0.005;
          particle.element.style.opacity = '0.15';
          particle.element.style.transform = `scale(${1 + force * 0.3})`;
        } else {
          particle.element.style.opacity = '0.08';
          particle.element.style.transform = 'scale(1)';
        }
      });
    };

    container.addEventListener('mousemove', handleMouseMove);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
      container.removeEventListener('mousemove', handleMouseMove);
      particles.forEach(particle => {
        if (particle.element.parentNode) {
          particle.element.parentNode.removeChild(particle.element);
        }
      });
    };
  }, []);

  return <EducationalParticlesContainer ref={containerRef} />;
};

// Helper function to get icon text representation
const getIconText = (iconName) => {
  const iconMap = {
    FiBook: '📚',
    FiBookOpen: '📖',
    FiStar: '⭐',
    FiAward: '🏆',
    FiTarget: '🎯',
    FiUsers: '👥',
  };
  
  return iconMap[iconName] || '📚';
};

export default EducationalParticles;
